/**
 * Keyboard bindings: the defaults, the player's overrides, and where they are
 * kept. Pure data with a localStorage shim; the panel that edits them lives in
 * `src/bindings-ui.ts`, the class that reads keys in `input.ts`.
 */
import type { KeyMap } from './input.js';

export type Action = keyof KeyMap;
export const ACTIONS: readonly Action[] = ['left', 'right', 'jump', 'attack'];
export const ACTION_LABELS: Record<Action, string> = {
  left: 'Left',
  right: 'Right',
  jump: 'Jump',
  attack: 'Smash',
};

export interface Bindings {
  p1: KeyMap;
  p2: KeyMap;
}

export const DEFAULT_BINDINGS: Bindings = {
  p1: { left: 'KeyA', right: 'KeyD', jump: 'KeyW', attack: 'Space' },
  p2: { left: 'ArrowLeft', right: 'ArrowRight', jump: 'ArrowUp', attack: 'ArrowDown' },
};

export const STORAGE_KEY = 'blobvolley.bindings';

interface Store {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function store(): Store | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

function isKeyMap(v: unknown): v is KeyMap {
  if (typeof v !== 'object' || v === null) return false;
  return ACTIONS.every((a) => typeof (v as Record<string, unknown>)[a] === 'string');
}

/** Parse a stored blob, falling back per player to the defaults. */
export function bindingsFromJSON(raw: unknown): Bindings {
  const out: Bindings = { p1: { ...DEFAULT_BINDINGS.p1 }, p2: { ...DEFAULT_BINDINGS.p2 } };
  if (typeof raw !== 'object' || raw === null) return out;
  const r = raw as Record<string, unknown>;
  if (isKeyMap(r['p1'])) out.p1 = { ...r['p1'] };
  if (isKeyMap(r['p2'])) out.p2 = { ...r['p2'] };
  return out;
}

export function loadBindings(s: Store | null = store()): Bindings {
  const raw = s?.getItem(STORAGE_KEY);
  if (!raw) return bindingsFromJSON(null);
  try {
    return bindingsFromJSON(JSON.parse(raw));
  } catch {
    return bindingsFromJSON(null);
  }
}

export function saveBindings(b: Bindings, s: Store | null = store()): void {
  s?.setItem(STORAGE_KEY, JSON.stringify(b));
}

export function resetBindings(s: Store | null = store()): Bindings {
  s?.removeItem(STORAGE_KEY);
  return bindingsFromJSON(null);
}

/**
 * Bind `code` to one action, clearing it from any other action of either
 * player so a key can never mean two things at once.
 */
export function rebind(b: Bindings, player: 'p1' | 'p2', action: Action, code: string): Bindings {
  const next: Bindings = { p1: { ...b.p1 }, p2: { ...b.p2 } };
  for (const p of ['p1', 'p2'] as const) {
    for (const a of ACTIONS) {
      if (next[p][a] === code && !(p === player && a === action)) next[p][a] = '';
    }
  }
  next[player][action] = code;
  return next;
}

/** Human name for a `KeyboardEvent.code`. */
export function keyLabel(code: string): string {
  if (!code) return '—';
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  if (code.startsWith('Arrow')) {
    return { Left: '←', Right: '→', Up: '↑', Down: '↓' }[code.slice(5)] ?? code;
  }
  const named: Record<string, string> = {
    Space: 'Space',
    ShiftLeft: 'L Shift',
    ShiftRight: 'R Shift',
    ControlLeft: 'L Ctrl',
    ControlRight: 'R Ctrl',
    AltLeft: 'L Alt',
    AltRight: 'R Alt',
    Enter: 'Enter',
    Tab: 'Tab',
    Backspace: 'Bksp',
    Semicolon: ';',
    Quote: "'",
    Comma: ',',
    Period: '.',
    Slash: '/',
  };
  return named[code] ?? code.replace(/^Numpad/, 'Num ');
}

/** "A D / W / Space": the HUD line for one key map. */
export function describeMap(m: KeyMap): string {
  return `${keyLabel(m.left)} ${keyLabel(m.right)} / ${keyLabel(m.jump)} / ${keyLabel(m.attack)}`;
}

/** Keys the browser would otherwise act on (scroll, click a focused button). */
export function swallows(code: string): boolean {
  return code === 'Space' || code.startsWith('Arrow') || code === 'Tab';
}
